import { ButtonLink, Label } from '../ExpandableLink/ExpandableLink.style';
import ExpandableCard, { ToggleProps } from '../ExpandableCard/ExpandableCard';
import { FunctionalComponent, h } from 'preact';
import { TCFVendor, isTcfLabels, isTcfUI } from '@usercentrics/cmp-browser-sdk';
import { RefContainer } from './VirtualList.style';
import { ScrollToId } from '../../App.helpers';
import { TabPanelTitle } from '../Tabs';
import VendorDescription from '../VendorDescription/VendorDescription';
import { memo } from 'preact/compat';
import { useRef } from 'preact/hooks';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { useVirtualListItem } from '../../hooks';

type ItemProps = {
  index: number;
  id: number;
  scrollToId: ScrollToId;
  getItemSize: (index: number) => number;
  setItemSize: (index: number, height: number) => void;
  title: string;
  onToggleConsent: (id: number, checked: boolean) => void;
  onToggleLegInterestConsent: (id: number, checked: boolean) => void;
  onToggleAll: () => void;
  vendors: TCFVendor[];
};

const legitimateInterestDefaultValue = true;
const consentDefaultValue = false;

const VirtualVendorItem: FunctionalComponent<ItemProps> = memo(
  ({
    id,
    index,
    scrollToId,
    getItemSize,
    setItemSize,
    title,
    vendors,
    onToggleAll,
    onToggleConsent,
    onToggleLegInterestConsent,
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const { updateComponentSize } = useVirtualListItem({ getItemSize, index, itemRef, setItemSize });
    const { labels, ui } = useSettingsContext();

    if (!labels || !isTcfLabels(labels) || !ui || !isTcfUI(ui)) {
      return null;
    }

    // Optional: render a title as a list item
    if (index === 0) {
      return (
        <RefContainer ref={itemRef}>
          <TabPanelTitle>{title}</TabPanelTitle>
          {ui.secondLayer.showToggleAllVendors && (
            <ButtonLink data-testid="uc-toggle-all-iab-vendors" onClick={onToggleAll}>
              <Label>{labels.vendor.toggleAll}</Label>
            </ButtonLink>
          )}
        </RefContainer>
      );
    }

    const toggles: ToggleProps[] = [];
    const vendorProps = vendors[id - 1];

    if (vendorProps.showLegitimateInterestConsentToggle) {
      toggles.push({
        ariaLabel: `${vendorProps.name} ${labels.secondLayer.vendorsTab} ${labels.ariaLabels.vendorLegIntToggle}`,
        checked: vendorProps.legitimateInterestConsent,
        defaultChecked: legitimateInterestDefaultValue,
        id: `legitimateInterestConsent${vendorProps.id}`,
        label: labels.toggles.legitimateInterest,
        onToggle: () =>
          onToggleLegInterestConsent(
            vendorProps.id,
            vendorProps.legitimateInterestConsent === null
              ? !legitimateInterestDefaultValue
              : !vendorProps.legitimateInterestConsent,
          ),
      });
    }

    if (vendorProps.showConsentToggle) {
      toggles.push({
        ariaLabel: `${vendorProps.name} ${labels.secondLayer.vendorsTab} ${labels.ariaLabels.vendorConsentToggle}`,
        checked: vendorProps.consent,
        defaultChecked: consentDefaultValue,
        id: `vendorConsent${vendorProps.id}`,
        label: labels.toggles.consent,
        onToggle: () => onToggleConsent(vendorProps.id, !vendorProps.consent),
      });
    }

    return (
      <RefContainer ref={itemRef}>
        <ExpandableCard
          hideToggle={false}
          isExpandedOnInit={vendorProps.id === scrollToId}
          onToggleExpandable={updateComponentSize}
          testid="uc-tcf-vendors-expandable"
          title={vendorProps.name}
          toggles={toggles}
          type={labels.secondLayer.vendorsTab}
        >
          <VendorDescription vendor={vendorProps} labels={labels} onToggleExpandable={updateComponentSize} />
        </ExpandableCard>
      </RefContainer>
    );
  },
);

export default VirtualVendorItem;
